"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";

import { cn } from "./utils";

function Checkbox({
  className,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer size-4 shrink-0 rounded-[4px] border shadow-xs outline-none transition-[color,background-color,border-color,box-shadow]",

        // Unchecked and actionable. A definite border, a pointer and a hover
        // tint, so an EMPTY box still reads as something you can click — the
        // default border token is too low-contrast on a dark row to say that
        // on its own.
        "cursor-pointer border-slate-400 bg-input-background hover:border-primary dark:border-white/40 dark:bg-input/30",

        "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary",

        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",

        // Disabled. Opacity alone was the whole rule before and it did not
        // read: a 50%-faded faint border still looks like a faint border. A
        // DASHED, unfilled box is legible as "not available" at a glance,
        // without relying on contrast or colour — which matters on the
        // findings table, where most rows are locked and the one selectable
        // row has to stand out.
        "disabled:cursor-not-allowed disabled:border-dashed disabled:bg-transparent disabled:opacity-70",
        "disabled:border-muted-foreground/40 disabled:hover:border-muted-foreground/40",
        "dark:disabled:border-white/25 dark:disabled:hover:border-white/25",

        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
